import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { User, Role } from 'app/auth/models';
import { ToastrService } from 'ngx-toastr';
import { AngularFireAuth, AngularFireAuthModule, TENANT_ID } from '@angular/fire/compat/auth';
import { UserManagementService } from 'app/main/apps/user-management/user-management.service';
import { ApiRoute } from 'environments/apiroute.prod';
import { Endpoint } from 'environments/endpoint.prod';
import { Router } from '@angular/router';
import { PermissionService } from 'app/main/apps/permission/permisson.service';
import { BeneficiaryManagementService } from 'app/main/apps/beneficiary-managment/beneficiary-management.service';
import { ConfigConstants } from 'app/main/apps/constants/config-constants';
import { DeviceFingerprintService } from './device-fingerprint.service';
import { AdminConfigService } from 'app/main/apps/services/admin-config.service';
import Swal from 'sweetalert2';


@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  //public
  public currentUser: Observable<User>;

  //private
  private currentUserSubject: BehaviorSubject<User>;
  public token : any;
  private refreshTokenPromise: Promise<string> | null = null;

  /**
   *
   * @param {HttpClient} _http
   * @param {ToastrService} _toastrService
   */
  constructor(private _http: HttpClient, private _toastrService: ToastrService,
    private _router: Router,
    public afAuth: AngularFireAuth,
    private _userManagementService: UserManagementService,
    private _beneficiaryMangement: BeneficiaryManagementService,
    private permissionService: PermissionService,
    private _deviceFingerprintService: DeviceFingerprintService, private _adminConfigService: AdminConfigService) {
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();

    // Keep the cached token in sync with Firebase (single source of truth).
    // afAuth.idToken (onIdTokenChanged) fires on sign-in, on every proactive or
    // forced refresh, AND when another tab refreshes the token (IndexedDB syncs
    // across tabs). Mirroring it into localStorage keeps the in-memory copy and
    // every consumer of currentUser.token fresh without each one refreshing itself.
    this.afAuth.idToken.subscribe((token) => {
      if (!token) {
        return; // sign-out is handled by logout()/onAuthStateChanged
      }
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return;
      }
      const user = JSON.parse(raw);
      if (user && user.token !== token) {
        user.token = token;
        this.token = token;
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
      }
    });
  }

  // getter: currentUserValue
  public get currentUserValue(): User {
    return JSON.parse(localStorage.getItem('currentUser'));
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Admin;
  }

  /**
   *  Confirms if user is client
   */
  get isClient() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Client;
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  async login(email: string, password: string) {
    return await this.SignIn(email, password);
    // return this._http
    //   .post<any>(`${environment.apiUrl}/users/authenticate`, { email, password })
    //   .pipe(
    //     map(user => {
    //       // login successful if there's a jwt token in the response
    //       if (user && user.token) {
    //         // store user details and jwt token in local storage to keep user logged in between page refreshes
    //         localStorage.setItem('currentUser', JSON.stringify(user));

    //         // Display welcome toast!
    //         setTimeout(() => {
    //           this._toastrService.success(
    //             'You have successfully logged in as an ' +
    //               user.role +
    //               ' user to LawTrax. Now you can start to explore. Enjoy! 🎉',
    //             '👋 Welcome, ' + user.firstName + '!',
    //             { toastClass: 'toast ngx-toastr', closeButton: true }
    //           );
    //         }, 2500);

    //         // notify
    //         this.currentUserSubject.next(user);
    //       }

    //       return user;
    //     })
    //   );
  }

  isAttorney(role: string) : boolean {

    return ConfigConstants.attorneyRoles.includes(role);

  } 
  async SignIn(email, password) {
    this.currentUserSubject.next(null);
    localStorage.removeItem('currentUser');
    return await this.afAuth.signInWithEmailAndPassword(email, password)
      .then(async (result) => {
        if (result && result.user) {
          // Get user from user-managemnt
          let bene, displayName,beneId, type,dependentID,applicants;
          this.token = await result.user.getIdToken(true);

          //  const tokenExpiryInfo = this.getTokenExpiryInfo(this.token);

          // Generate and store device fingerprint
          const deviceId = await this._deviceFingerprintService.getDeviceFingerprint();
          this._deviceFingerprintService.storeDeviceId(deviceId);
        
          let headers = new HttpHeaders({
            'Client': await this.afAuth.tenantId,
            'Authorization': `Bearer ${this.token}`,
            'X-Device-ID': deviceId
          });
          let options = { headers: headers };

          // Get user info first to check role
          const lawtraxUser = await this._userManagementService.handleLogin({userId: result.user.uid}, options);
          
          const tenantId = result.user?.tenantId || await this.afAuth.tenantId || '';

          // Skip device session validation for Employer and Beneficiary roles
          if (lawtraxUser.role !== 'Employer' && lawtraxUser.role !== 'Beneficiary') {
            // Validate device session with backend for other roles
            try {
              const ipAddress = await this._deviceFingerprintService.getClientIpAddress();
              const userAgent = navigator.userAgent;

              const validationResponse = await this._adminConfigService.validateDeviceSession(
                result.user.uid,
                deviceId,
                ipAddress,
                userAgent,
                tenantId,
                options
              );

              console.log('Device session validation result:', validationResponse);

              // Check if session found - other active sessions exist
              if (validationResponse && validationResponse['valid'] === false) {
                console.warn('Session found on another device:', validationResponse['reason']);

                // Wait for user action on modal before proceeding
                const res = await Swal.fire({
                  title: "⚠️ Session Found",
                  text: "Your account is already logged in on another device.",
                  html: "<p>Your account is already logged in on another device.</p><p style='margin-top: 10px;'><strong>Choose an action:</strong></p>",
                  showCancelButton: true,
                  confirmButtonText: 'Logout Other Sessions',
                  cancelButtonText: 'Cancel',
                  icon: 'warning'
                });

                // Handle user action
                if (res.isConfirmed) {
                  // User wants to logout other sessions
                  try {
                    let logoutData = await this._adminConfigService.logoutAllSessions(result.user.uid, tenantId, options);
                    if (logoutData && logoutData['count'] > 0) {
                      this._toastrService.success('Other sessions have been logged out.');
                      // Re-validate device session after logout
                      const revalidationResponse = await this._adminConfigService.validateDeviceSession(
                        result.user.uid,
                        deviceId,
                        ipAddress,
                        userAgent,
                        tenantId,
                        options
                      );
                      console.log('Device session re-validation result:', revalidationResponse);
                    }
                  } catch (logoutError) {
                    console.error('Error during logout of other sessions:', logoutError);
                    this._toastrService.error('Failed to logout other sessions.');
                  }
                } else if (res.isDismissed) {
                  // User cancelled - do not proceed with login
                  throw new Error('Login cancelled by user due to existing active session on another device.');
                }
              }
            } catch (validationError) {
              console.error('Error during device session validation:', validationError);
              // Optionally handle validation error - continue or block login
              throw validationError;
            }
          }
          if(lawtraxUser.role == 'Beneficiary'){
            bene = await this._beneficiaryMangement.getSpouseEmail({email:lawtraxUser.emailAddress}, options);
            type = bene.beneficiaryType,
            dependentID = bene && bene.dependent ?  bene.dependent.beneficiaryId : '';
            applicants = bene && bene.applicants ?  bene.applicants : null;
            displayName =  (lawtraxUser.subRole !== 'Individual' ? lawtraxUser?.customer?.customerName : ( bene.dependent ? bene.dependent?.beneficiaryFirstName + ' ' +bene.dependent?.beneficiaryLastName : '' ) );
           beneId = bene?.beneficiaryId; 
          } else if(lawtraxUser.role == 'Employer'){
            type = lawtraxUser.role

          }
          //  else if (lawtraxUser.role == 'Beneficiary' && lawtraxUser.customer){
          //   displayName = lawtraxUser?.customer?.customerName;
          //  }
          
          const user = <User> {
            email: lawtraxUser.emailAddress,
            firstName: lawtraxUser.firstName,
            lastName: lawtraxUser.lastName,
            displayName: displayName ? displayName  :'',
            type : type ? type : '',
            customerId:lawtraxUser?.customerId || '',
            id: result.user.uid,
            password: password,
            token: this.token,
            beneId: beneId,
            dependentId: dependentID,
            applicants: applicants ? applicants : null,
            role: lawtraxUser.role as Role,
            subRole: lawtraxUser.subRole ? lawtraxUser.subRole : '',
            avatar: result.user.photoURL ?? 'default-user-icon.png',
            tenantId: await this.afAuth.tenantId,
            permissions: lawtraxUser.permissions
          }
          this.permissionService.setCurrentUserPermissions(lawtraxUser.permissions)
          localStorage.setItem('currentUser', JSON.stringify(user));
          // Display welcome toast!
          setTimeout(() => {
            this._toastrService.success(
              'You have successfully logged in as an ' +
                user.role +
                ' user to LawTrax. Now you can start to explore. Enjoy! 🎉',
              '👋 Welcome, ' + user.firstName + '!',
              { toastClass: 'toast ngx-toastr', closeButton: true ,timeOut: 500,}
            );
          }, 2500);
          // notify
          this.currentUserSubject.next(user);

          return user;
        }
      }).catch((error) => {
        window.alert(error.message)
      })
  }

  async signInWithToken(jwtToken){
    let res = <any>await this._http.post(`${Endpoint.LEAD_MICROSERVICE + ApiRoute.EXCHANGE_ESIGN_ANONYMOUS_TOKEN}`, {code: jwtToken}).toPromise();
    let token = res.token
    return await this.afAuth.signInWithCustomToken(token).then(async (result) => {
      let token = await result.user.getIdToken()
      let tenantId = result.user.tenantId
      let anonymousUser = result.user
      const user = <User> {
        email: '',
        firstName: 'user',
        lastName: '',
        customerId:'',
        id: anonymousUser.uid,
        password: '',
        token: await anonymousUser.getIdToken(),
        role: Role.Client,
        avatar: anonymousUser.photoURL ?? 'default-user-icon.png',
        tenantId: await this.afAuth.tenantId
      }
      
      localStorage.setItem('currentUser', JSON.stringify(user));
      this.currentUserSubject.next(user);
      return user; // CRITICAL: Return user object so component knows authentication succeeded
    }).catch((error) => {
      window.alert(error.message)
    })
  }

  /**
   * Refresh token.
   *
   * Single-flight: concurrent callers share one in-flight refresh so we don't
   * fire multiple parallel getIdToken(true) calls (which causes Firebase rate
   * limiting and races where one wins and the others throw → premature logout).
   */
  refreshToken(): Promise<string> {
    if (!this.currentUserValue) {
      return Promise.reject(new Error('No current user'));
    }
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = (async () => {
      const user = await this.afAuth.currentUser;
      if (!user) {
        throw new Error('Firebase user not available');
      }
      const newToken = await user.getIdToken(true);
      this.token = newToken;

      const localUser = JSON.parse(localStorage.getItem('currentUser'));
      if (localUser) {
        localUser.token = newToken;
        localStorage.setItem('currentUser', JSON.stringify(localUser));
        this.currentUserSubject.next(localUser);
      }
      return newToken;
    })();

    this.refreshTokenPromise.finally(() => {
      this.refreshTokenPromise = null;
    });

    return this.refreshTokenPromise;
  }

  /**
   * Extract token expiry information from JWT
   * @param token JWT token string
   * @returns Token expiry details
   */
  public getTokenExpiryInfo(token: string): any {
    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid token format');
        return null;
      }

      // Decode payload (add padding if needed)
      const payload = parts[1];
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = JSON.parse(atob(paddedPayload));

      const expiryTimeUnix = decoded.exp; // exp is in seconds
      const issuedAtUnix = decoded.iat; // iat is in seconds
      const currentTimeUnix = Math.floor(Date.now() / 1000);
      
      const expiryTimeISO = new Date(expiryTimeUnix * 1000).toISOString();
      const issuedAtISO = new Date(issuedAtUnix * 1000).toISOString();
      const timeUntilExpirySeconds = expiryTimeUnix - currentTimeUnix;
      const timeUntilExpiryMinutes = Math.floor(timeUntilExpirySeconds / 60);
      const ttlSeconds = expiryTimeUnix - issuedAtUnix;

      return {
        expiryTimeUnix,
        issuedAtUnix,
        currentTimeUnix,
        expiryTimeISO,
        issuedAtISO,
        timeUntilExpirySeconds,
        timeUntilExpiryMinutes,
        ttlSeconds,
        isExpired: timeUntilExpirySeconds <= 0
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
  
  /**
   * User logout
   * Logs out from backend and clears local session
   */
  async logout() {
    try {
      // Get current user details before clearing storage
      const currentUser = this.currentUserValue;
      if (currentUser && currentUser.id) {
        const deviceId = this._deviceFingerprintService.getStoredDeviceId();
        if (deviceId) {
          // Call backend to logout device session
          await this._adminConfigService.logoutDeviceSession(currentUser.id, deviceId, currentUser.tenantId).catch((error) => {
            console.warn('Error logging out device session from backend:', error);
            // Continue with local logout even if backend call fails
          });
        }
      }
    } catch (error) {
      console.error('Error during logout process:', error);
    } finally {
      // Always clear local session regardless of backend call
      this.currentUserSubject.next(null);
      localStorage.removeItem('currentUser');
      localStorage.removeItem('master_lookup');
      localStorage.removeItem('caseConfig');
      sessionStorage.clear();
    }
  }
}
